import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import { Circle, GeoJSON, Marker, Pane, useMap, useMapEvents } from 'react-leaflet';
import Store from '../store/Store';
import { IconClose } from './Icons';

const polygonRadius = 250;

const greenIcon = new L.Icon({
	iconUrl:
		'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
	shadowUrl:
		'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	shadowSize: [41, 41],
});

const redIcon = new L.Icon({
	iconUrl:
		'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
	shadowUrl:
		'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	shadowSize: [41, 41],
});

const layers: Record<string, L.Layer> = {};

interface LeafletGeoSetup {
	map?: L.Map;
	onSaveGeo?: (values: (number | string)[]) => void;
}

export interface LeafletGeoProps {
	setup: LeafletGeoSetup;
}

interface GeoMeshItem {
	uuid: string;
	droneStationLatitude?: number;
	droneStationLongitude?: number;
	droneDiameter?: number;
}

interface GeoJSONState {
	_center?: { lat: number; lng: number };
	_key?: string;
	_isAllStations?: boolean;
	_isViewport?: boolean;
	_uuid?: string;
	[key: string]: unknown;
}

interface GeoFeature {
	id: string;
	_selected?: boolean;
	properties: Record<string, unknown>;
	_tooltip?: string;
}

function setLayerStyle(layer: L.Layer & { setStyle: (s: object) => void; bringToFront?: () => void; bringToBack?: () => void }, feature: GeoFeature) {
	if (!layer) return;
	const value = (feature.properties.areaBasePrice as number) ?? 0;
	const color = Math.min(0.1 + value / 200, 0.8);
	const style = {
		weight: feature._selected ? 3 : ((feature.properties.weight as number) || 1),
		color: feature._selected ? '#00ff00' : (feature.properties.stroke as string),
		fillColor: value ? '#00ff00' : (feature.properties.fill as string),
		fillOpacity: value ? color : (feature.properties['fill-opacity'] as number),
	};
	layer.setStyle(style);
	if (feature._selected) {
		layer.bringToFront?.();
	} else {
		layer.bringToBack?.();
	}
}

export default function LeafletGeo(props: LeafletGeoProps) {
	const [
		features,
		geoJSON,
		geoMode,
		geoCenter,
		snappedCenter,
		geoMeshes,
		selectedDrone,
	] = Store.use(
		'features',
		'geoJSON',
		'geoMode',
		'geoCenter',
		'snappedCenter',
		'geoMeshes',
		'selectedDrone'
	) as [
		Record<string, GeoFeature>,
		GeoJSONState | null,
		string,
		{ lat: number; lng: number } | null,
		{ lat: number; lng: number } | null,
		GeoMeshItem[] | undefined,
		{ reachDiameter?: number } | null,
	];

	const selectedMode = useRef<string | null>(null);
	const map = useMap();

	useEffect(() => {
		props.setup.map = map;
		map.touchZoom.disable();
		map.doubleClickZoom.disable();
		map.boxZoom.disable();
		map.on('mouseup', () => {
			selectedMode.current = null;
		});
	}, []);

	useEffect(() => {
		if (!geoJSON) return;

		if (geoJSON._isAllStations || geoJSON._isViewport) {
			try {
				const bounds = L.geoJSON(geoJSON as unknown as GeoJSON.GeoJsonObject).getBounds();
				if (bounds.isValid()) {
					map.fitBounds(bounds, { padding: [24, 24] });
					return;
				}
			} catch {
				// Fallback to center-based view below.
			}
		}

		if (geoJSON._center) map.setView(geoJSON._center, 12);
	}, [geoJSON, map]);

	useMapEvents({
		click: (e: L.LeafletMouseEvent) => {
			if (geoMode === 'edit' || geoJSON) return;
			Store.set('geoCenter', e.latlng);
			Store.snapToGrid(e.latlng.lat, e.latlng.lng);
		},
	});

	const eventHandlers = useMemo(
		() => ({
			drag(e: L.LeafletEvent & { target: L.Marker }) {
				Store.set('geoCenter', e.target.getLatLng());
			},
			dragend(e: L.LeafletEvent & { target: L.Marker }) {
				const latlng = e.target.getLatLng();
				Store.snapToGrid(latlng.lat, latlng.lng);
			},
		}),
		[]
	);

	const onEachFeature = (feature: GeoFeature & { id?: string | number }, layer: L.Layer & { on: (e: string, fn: () => void) => void; bindTooltip: (c: string, o: object) => void; setStyle: (s: object) => void; bringToFront?: () => void; bringToBack?: () => void; feature?: GeoFeature }) => {
		const fid = feature.id != null ? String(feature.id) : '';
		layers[fid] = layer;
		const f = feature as GeoFeature;
		f.id = fid || (f.id ?? '');
		(layer as L.Layer & { feature?: GeoFeature }).feature = f;
		layer.on('mouseover', onLayerOver as () => void);
		layer.on('mousedown', onLayerDown as () => void);
		f.properties.areaBasePrice = (f.properties.areaBasePrice as number) ?? 0;
		f.properties.decisionFee = (f.properties.decisionFee as number) ?? 0;
		f.properties.applicationFee = (f.properties.applicationFee as number) ?? 0;
		f._tooltip =
			f.id +
			': ' +
			feature.properties.areaBasePrice +
			' | ' +
			f.properties.decisionFee +
			' | ' +
			f.properties.applicationFee;
		layer.bindTooltip(f._tooltip, { direction: 'top', sticky: true });
		setLayerStyle(layer, f);
	};

	function onLayerDown(e: L.LeafletMouseEvent & { target: L.Layer & { feature?: GeoFeature } }) {
		if (!e.originalEvent.shiftKey) return;
		e.originalEvent.stopPropagation();
		e.originalEvent.preventDefault();
		if (e.target.feature?._selected) {
			selectedMode.current = 'remove';
			unselectLayer(e.target);
		} else {
			selectedMode.current = 'add';
			selectLayer(e.target);
		}
	}

	function onLayerOver(e: L.LeafletMouseEvent & { target: L.Layer & { feature?: GeoFeature } }) {
		if (!e.originalEvent.shiftKey || !selectedMode.current) return;
		e.originalEvent.stopPropagation();
		e.originalEvent.preventDefault();
		if (selectedMode.current === 'remove') {
			if (e.target.feature?._selected) unselectLayer(e.target);
		} else if (selectedMode.current === 'add') {
			if (!e.target.feature?._selected) selectLayer(e.target);
		}
	}

	function selectLayer(layer: L.Layer & { feature?: GeoFeature; setStyle: (s: object) => void; bringToFront?: () => void }) {
		const feature = layer.feature;
		if (!feature) return;
		feature._selected = true;
		layer.setStyle({ weight: 3, color: '#00ff00' });
		layer.bringToFront?.();
		const currentFeatures = Store.get().features as Record<string, GeoFeature>;
		currentFeatures[feature.id] = feature;
		Store.set('features', { ...currentFeatures });
	}

	function unselectLayer(layer: L.Layer & { feature?: GeoFeature; setStyle: (s: object) => void; bringToBack?: () => void }) {
		const feature = layer.feature;
		if (!feature) return;
		feature._selected = false;
		layer.setStyle({
			weight: (feature.properties.weight as number) || 1,
			color: feature.properties.stroke as string,
		});
		layer.bringToBack?.();
		const currentFeatures = Store.get().features as Record<string, GeoFeature>;
		delete currentFeatures[feature.id];
		Store.set('features', { ...currentFeatures });
	}

	const resetGeoJSON = () => {
		Object.keys(layers).forEach((k) => delete layers[k]);
		Store.set({ geoJSON: null, features: {} });
		Store.cache?.removeItem('geoJSON');
		Store.loadAllStationsCells();
	};

	props.setup.onSaveGeo = (values: (number | string)[]) => {
		Object.values(features ?? {}).forEach((feature) => {
			feature.properties.areaBasePrice = values[0];
			feature.properties.decisionFee = values[1];
			feature.properties.applicationFee = values[2];
			feature._tooltip =
				feature.id +
				': ' +
				feature.properties.areaBasePrice +
				' | ' +
				feature.properties.decisionFee +
				' | ' +
				feature.properties.applicationFee;
			const layerRef = layers[feature.id];
			if (layerRef && 'setTooltipContent' in layerRef) {
				(layerRef as L.Layer & { setTooltipContent?: (c: string) => void }).setTooltipContent?.(feature._tooltip);
			}
			setLayerStyle(layerRef as L.Layer & { setStyle: (s: object) => void; bringToFront?: () => void; bringToBack?: () => void }, feature);
		});
		Store.saveCells(features);
	};

	return (
		<>
			<Pane name="dronePane" style={{ zIndex: 500 }} />
			{geoMode === 'create' && geoCenter && (
				<>
					<Marker
						position={geoCenter}
						eventHandlers={eventHandlers}
						icon={redIcon}
						zIndexOffset={1000}
					/>
					{selectedDrone?.reachDiameter != null && (
						<Circle
							center={geoCenter}
							radius={Math.max(0, (selectedDrone.reachDiameter ?? 0) / 2)}
							pane="dronePane"
							pathOptions={{
								color: '#cc0000',
								fillColor: '#cc0000',
								fillOpacity: 0.04,
								weight: 2,
							}}
						/>
					)}
					{snappedCenter && (
						<>
							<Marker position={snappedCenter} icon={greenIcon} />
							<Circle
								center={snappedCenter}
								radius={polygonRadius}
								pathOptions={{
									color: '#22c55e',
									fillColor: '#22c55e',
									fillOpacity: 0.1,
									weight: 2,
									dashArray: '5, 5',
								}}
							/>
						</>
					)}
				</>
			)}

			{geoMode === 'edit' && geoJSON && (
				<>
					<GeoJSON
						key={
							geoJSON._key ??
							(geoJSON._isAllStations ? 'all-stations' : geoJSON._uuid)
						}
						data={geoJSON as unknown as GeoJSON.GeoJsonObject}
						onEachFeature={(feature: unknown, layer: unknown) =>
							onEachFeature(feature as GeoFeature, layer as L.Layer & { on: (e: string, fn: () => void) => void; bindTooltip: (c: string, o: object) => void; setStyle: (s: object) => void; bringToFront?: () => void; bringToBack?: () => void; feature?: GeoFeature })
						}
					/>
					<button
						className="leaflet-button top-12 right-3"
						data-pw="button-reset-geojson"
						aria-label="Kartenauswahl zurücksetzen"
						onClick={resetGeoJSON}
						type="button"
					>
						<IconClose />
					</button>
					{geoMeshes
						?.filter((m) => {
							if (m?.droneStationLatitude == null || m?.droneStationLongitude == null)
								return false;
							if (geoJSON._isAllStations || geoJSON._isViewport) return true;
							return geoJSON._uuid && m.uuid === geoJSON._uuid;
						})
						.map((m) => (
							<React.Fragment key={`drone-${m.uuid}`}>
								<Marker
									position={{
										lat: m.droneStationLatitude!,
										lng: m.droneStationLongitude!,
									}}
									icon={redIcon}
									zIndexOffset={1000}
								/>
								{m.droneDiameter != null && (
									<Circle
										center={{
											lat: m.droneStationLatitude!,
											lng: m.droneStationLongitude!,
										}}
										radius={Math.max(0, m.droneDiameter / 2)}
										pane="dronePane"
										pathOptions={{
											color: '#cc0000',
											fillColor: '#cc0000',
											fillOpacity: 0.04,
											weight: 2,
										}}
									/>
								)}
							</React.Fragment>
						))}
				</>
			)}
		</>
	);
}
